import {Injectable} from '@angular/core';
import {TranslocoService} from '@ngneat/transloco';
import {ConfirmationService, MessageService} from 'primeng/api';
import {HttpErrorResponse, HttpStatusCode} from "@angular/common/http";

@Injectable({
  providedIn: 'root'
})
export class CommonMessagesService {

  constructor(private i18n: TranslocoService,
              private confirmationService: ConfirmationService,
              private messageService: MessageService) {
  }

  public showDeleteRequestConfirmation(acceptCallback): void {
    this.confirmationService.confirm({
      icon: 'pi pi-exclamation-triangle',
      message: this.i18n.translate('info.confirmDelete'),
      header: this.i18n.translate('forms.confirmation'),
      acceptLabel: this.i18n.translate('common.accept'),
      rejectLabel: this.i18n.translate('common.cancel'),
      acceptIcon: 'pi pi-check',
      defaultFocus: 'none',
      acceptButtonStyleClass: ' p-button-text',
      accept: acceptCallback
    });
  }

  public showUpdateFirmwareRequestConfirmation(acceptCallback): void {
    this.confirmationService.confirm({
      icon: 'pi pi-exclamation-triangle',
      message: this.i18n.translate('info.confirmUpdateFirmware'),
      header: this.i18n.translate('forms.confirmation'),
      acceptLabel: this.i18n.translate('common.accept'),
      rejectLabel: this.i18n.translate('common.cancel'),
      acceptIcon: 'pi pi-check',
      defaultFocus: 'none',
      acceptButtonStyleClass: ' p-button-text',
      accept: acceptCallback
    });
  }

  public showCopiedToClipboardMessage(): void {
    const summary = this.i18n.translate('success.generic.clipboard');
    const detail = this.i18n.translate('success.copy.clipboard');
    this.messageService.add({severity: 'success', summary, detail, sticky: false});
  }

  public showSuccessfullyDeletedMessage(): void {
    const summary = this.i18n.translate('success.generic.changesApplied');
    const detail = this.i18n.translate('success.generic.deleted');
    this.messageService.add({severity: 'success', summary, detail, sticky: false});
  }

  public showSuccessfullyResetMessage(): void {
    const summary = this.i18n.translate('success.generic.changesApplied');
    const detail = this.i18n.translate('success.alarm.reset');
    this.messageService.add({severity: 'success', summary, detail, sticky: false});
  }

  public showElasticSearchMeasuresError(): void {
    const summary = this.i18n.translate('error.summary.unexpected');
    const detail = this.i18n.translate('error.details.measures');
    this.messageService.add({severity: 'error', summary, detail, sticky: true});
  }

  public showErrorWhileDeletingMessage(httpError: number): void {
    let summary;
    let detail;

    if (httpError == 409) {
      summary = this.i18n.translate('error.summary.restricted');
      detail = this.i18n.translate('error.delete.restricted');
    } else if (httpError == 403 || httpError == 401) {
      summary = this.i18n.translate('error.summary.restricted');
      detail = this.i18n.translate('error.delete.forbidden');
    } else if (httpError == 404) {
      summary = this.i18n.translate('error.summary.notFound');
      detail = this.i18n.translate('error.delete.notFound');
    } else if (httpError == 502) {
      summary = this.i18n.translate('error.delete.unexpected');
      detail = this.i18n.translate('error.delete.gateway');
    } else {
      summary = this.i18n.translate('error.summary.unexpected');
      detail = this.i18n.translate('error.delete.unexpected');
    }

    this.messageService.add({severity: 'error', summary, detail, sticky: true});
  }

  public showErrorWhileResettingAlarmMessage(): void {
    const summary = this.i18n.translate('error.summary.unexpected');
    const detail = this.i18n.translate('error.alarm.reset');
    this.messageService.add({severity: 'error', summary, detail, sticky: true});
  }

  public showRequestConfirmation(message: string, acceptCallback, rejectCallback?): void {
    this.confirmationService.confirm({
      icon: 'pi pi-exclamation-triangle',
      message: this.i18n.translate(message),
      header: this.i18n.translate('forms.confirmation'),
      acceptLabel: this.i18n.translate('common.accept'),
      rejectLabel: this.i18n.translate('common.cancel'),
      acceptIcon: 'pi pi-check',
      defaultFocus: 'none',
      acceptButtonStyleClass: 'p-button-text',
      accept: acceptCallback,
      reject: rejectCallback
    });
  }

  public showCancelRequestConfirmation(acceptCallback, rejectCallback?): void {
    this.confirmationService.confirm({
      icon: 'pi pi-exclamation-triangle',
      message: this.i18n.translate('info.confirmCancel'),
      header: this.i18n.translate('forms.confirmation'),
      acceptLabel: this.i18n.translate('common.accept'),
      rejectLabel: this.i18n.translate('common.cancel'),
      acceptIcon: 'pi pi-check',
      defaultFocus: 'none',
      acceptButtonStyleClass: 'p-button-text',
      accept: acceptCallback,
      reject: rejectCallback
    });
  }

  public showMoveMachineConfirmation(acceptCallback): void {
    this.confirmationService.confirm({
      icon: 'pi pi-exclamation-triangle',
      message: this.i18n.translate('info.confirmMoveMachine'),
      header: this.i18n.translate('forms.confirmation'),
      acceptLabel: this.i18n.translate('common.accept'),
      rejectLabel: this.i18n.translate('common.cancel'),
      acceptIcon: 'pi pi-check',
      defaultFocus: 'none',
      acceptButtonStyleClass: ' p-button-text',
      accept: acceptCallback
    });
  }

  public showSuccessfullyCreatedMessage(): void {
    const summary = this.i18n.translate('success.generic.changesApplied');
    const detail = this.i18n.translate('success.generic.created');
    this.messageService.add({severity: 'success', summary, detail, sticky: false});
  }

  public showSuccessfullyUpdatedMessage(): void {
    const summary = this.i18n.translate('success.generic.changesApplied');
    const detail = this.i18n.translate('success.generic.updated');
    this.messageService.add({severity: 'success', summary, detail, sticky: false});
  }

  public showPersistingError(mode: 'create' | 'edit', error: HttpErrorResponse, identifier: string): void {
    if (error.status === HttpStatusCode.Conflict) {
      this.showEntityAlreadyExistsError(mode, identifier);
    } else if (error.status === HttpStatusCode.Forbidden) {
      this.showForbiddenError(identifier);
    } else if (error.status === HttpStatusCode.BadGateway) {
      this.showGatewayCommunicationError(mode);
    } else {
      this.showUnexpectedPersistingError(mode);
    }
  }

  public showEntityAlreadyExistsError(mode: 'create' | 'edit', identifier: string): void {
    const summary = this.i18n.translate('error.' + mode + '.generic');
    const detail = this.i18n.translate('error.persist.alreadyExists', {identifier});
    this.messageService.add({severity: 'error', summary, detail, sticky: true});
  }

  public showForbiddenError(identifier: string): void {
    const summary = this.i18n.translate('error.summary.restricted');
    const detail = this.i18n.translate('error.persist.forbidden', {identifier});
    this.messageService.add({severity: 'error', summary, detail, sticky: true});
  }

  public showGatewayCommunicationError(mode: 'create' | 'edit'): void {
    const summary = this.i18n.translate('error.' + mode + '.generic');
    const detail = this.i18n.translate('error.persist.gateway');
    this.messageService.add({severity: 'error', summary, detail, sticky: true});
  }

  public showUnexpectedPersistingError(mode: 'create' | 'edit'): void {
    const summary = this.i18n.translate('error.summary.unexpected');
    const detail = this.i18n.translate('error.' + mode + '.unexpected');
    this.messageService.add({severity: 'error', summary, detail, sticky: true});
  }

  public showCustomError(summary: string, detail: string): void {
    this.messageService.add({severity: 'error', summary, detail, sticky: true});
  }

}
